import { sileroVADStreamingService } from "./silero-vad-streaming.service";
import { debugLog } from "../common/utils/debug";
import {
  MediaDevicesError,
  CustomEventListener,
  MicrophoneErrorDetail,
  RecordingErrorDetail,
  RecordingStatusDetail,
} from "../types";
import {
  MICROPHONE_CONSTRAINTS,
  SPEECH_TO_TEXT_ERROR_MESSAGES,
} from "../common/constants/audio.constants";
import { WINDOW_EVENTS } from "../common/constants/app.constants";

/**
 * Service for managing speech-to-text with Silero VAD in streaming mode
 */
class SpeechToTextSileroService {
  private isRecording = false;
  private isBlocked = false; // Prevents restart during avatar speech
  private eventListeners: CustomEventListener[] = [];

  constructor() {
    this.setupEventListeners();
  }

  /**
   * Pre-initialize audio pipeline to eliminate setup delay
   */
  async initialize(): Promise<void> {
    await sileroVADStreamingService.initialize();
  }

  /**
   * Request microphone permission from the user
   */
  async requestMicrophonePermission(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: MICROPHONE_CONSTRAINTS.ECHO_CANCELLATION,
          noiseSuppression: MICROPHONE_CONSTRAINTS.NOISE_SUPPRESSION,
          autoGainControl: MICROPHONE_CONSTRAINTS.AUTO_GAIN_CONTROL,
          sampleRate: MICROPHONE_CONSTRAINTS.SAMPLE_RATE,
          channelCount: MICROPHONE_CONSTRAINTS.CHANNEL_COUNT,
        },
      });

      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (error: any) {
      this.handleMicrophonePermissionError(error);
      return false;
    }
  }

  /**
   * Start recording with streaming transcription
   */
  async startRecording(): Promise<void> {
    if (this.isRecording || this.isBlocked) {
      debugLog('⏭️ [SpeechToText] Skipping start - recording:', this.isRecording, 'blocked:', this.isBlocked);
      return;
    }

    try {
      if (!(await this.requestMicrophonePermission())) {
        throw new Error("Microphone permission required");
      }

      await sileroVADStreamingService.start();

      this.isRecording = true;

      this.dispatchRecordingStatusEvent(true);
    } catch (error: any) {
      this.isRecording = false;
      this.handleRecordingError(error);
    }
  }

  /**
   * Block microphone from starting (used during avatar speech)
   */
  blockMicrophone(): void {
    // eslint-disable-next-line no-console
    debugLog('🚫 [SpeechToText] Blocking microphone');
    this.isBlocked = true;
    this.stopRecording();
  }

  /**
   * Unblock and restart microphone
   */
  async unblockMicrophone(): Promise<void> {
    // eslint-disable-next-line no-console
    debugLog('✅ [SpeechToText] Unblocking microphone');
    this.isBlocked = false;
    await this.startRecording();
  }

  /**
   * Clear microphone block without restarting recording.
   * Use this when mute state should control whether recording restarts.
   */
  clearBlock(): void {
    // eslint-disable-next-line no-console
    debugLog('🔓 [SpeechToText] Clearing microphone block (not auto-restarting)');
    this.isBlocked = false;
  }

  /**
   * Check if user is currently speaking (speech is active)
   * Used to prevent interrupting user's speech when avatar starts speaking
   */
  isUserSpeaking(): boolean {
    return sileroVADStreamingService.isUserSpeaking();
  }

  /**
   * Stop recording
   */
  stopRecording(): void {
    if (!this.isRecording) {
      return;
    }

    this.isRecording = false;
    sileroVADStreamingService.stop();

    this.dispatchRecordingStatusEvent(false);
  }

  /**
   * Set mute state - controls whether audio is sent to backend
   */
  setMuted(muted: boolean): void {
    sileroVADStreamingService.setMuted(muted);
  }

  /**
   * Clean up all resources
   */
  cleanup(): void {
    this.stopRecording();
    sileroVADStreamingService.destroy();

    this.eventListeners.forEach(({ event, handler }) => {
      window.removeEventListener(event, handler);
    });
    this.eventListeners = [];

    this.isRecording = false;
  }

  /**
   * Setup event listeners (placeholder for future use)
   */
  private setupEventListeners(): void {
    this.eventListeners = [];
  }

  /**
   * Handle microphone permission errors
   */
  private handleMicrophonePermissionError(error: any): void {
    const mediaError = error as MediaDevicesError;
    const errorMessage = this.getMicrophoneErrorMessage(mediaError.name);

    const eventDetail: MicrophoneErrorDetail = {
      error: errorMessage,
      originalError: mediaError,
    };

    window.dispatchEvent(
      new CustomEvent("microphone_error", { detail: eventDetail })
    );
  }

  /**
   * Handle recording errors
   */
  private handleRecordingError(error: any): void {
    const errorMessage = error.message?.includes("permission")
      ? SPEECH_TO_TEXT_ERROR_MESSAGES.PERMISSION_REQUIRED
      : SPEECH_TO_TEXT_ERROR_MESSAGES.RECORDING_FAILED;

    const eventDetail: RecordingErrorDetail = {
      error: errorMessage,
      originalError: error,
    };

    window.dispatchEvent(
      new CustomEvent("recording_error", { detail: eventDetail })
    );

    this.cleanup();
  }

  /**
   * Get user-friendly error message based on error type
   */
  private getMicrophoneErrorMessage(errorName: string): string {
    if (errorName === "NotFoundError" || errorName === "DevicesNotFoundError") {
      return SPEECH_TO_TEXT_ERROR_MESSAGES.NOT_FOUND;
    }

    if (
      errorName === "NotAllowedError" ||
      errorName === "PermissionDeniedError"
    ) {
      return SPEECH_TO_TEXT_ERROR_MESSAGES.NOT_ALLOWED;
    }

    return SPEECH_TO_TEXT_ERROR_MESSAGES.ACCESS_FAILED;
  }

  /**
   * Dispatch recording status change event
   */
  private dispatchRecordingStatusEvent(isRecording: boolean): void {
    const eventDetail: RecordingStatusDetail = { isRecording };
    window.dispatchEvent(
      new CustomEvent(WINDOW_EVENTS.RECORDING_STATUS_CHANGED, {
        detail: eventDetail,
      })
    );
  }
}

export const speechToTextSileroService = new SpeechToTextSileroService();
